/** Paging of a list and the page numbers shown under it */

/** Items per page, three rows of four tiles on a wide screen */
export const PAGE_SIZE: number = 12;

export const PAGE_NEIGHBOURS: number = 1;

/** A page of a list and the page numbers to show under it */
export interface PaginationResult<T> {
  listItem: Array<T>;
  /** Requested page, kept between 1 and "pageCount" */
  currentPage: number;
  pageCount: number;
  listPageEntry: Array<number>;
}

/**
 * @param listItem Whole list to page
 * @param currentPage Requested page, from 1; a page after the end becomes the last one
 * @param pageSize Items per page
 * @param pageNeighbours How many page numbers are shown around the current one
 */
export function paginate<T>(
  listItem: Array<T>,
  currentPage: number,
  pageSize: number = PAGE_SIZE,
  pageNeighbours: number = PAGE_NEIGHBOURS
): PaginationResult<T> {
  const listAllItem: Array<T> = listItem || [];

  const pageCount: number = Math.max(
    1,
    Math.ceil(listAllItem.length / pageSize)
  );

  const resolvedPage: number = Math.min(Math.max(currentPage, 1), pageCount);
  const startIndex: number = (resolvedPage - 1) * pageSize;

  return {
    listItem: listAllItem.slice(startIndex, startIndex + pageSize),
    currentPage: resolvedPage,
    pageCount,
    listPageEntry: buildPageEntries(resolvedPage, pageCount, pageNeighbours),
  };
}

/**
 * Also used directly when the paging is done by the server, which returns one page and the total
 * @param currentPage Page the numbers are built around
 * @param pageCount Number of pages
 * @param pageNeighbours How many page numbers surround the current one
 * @returns The page numbers in rendering order, null for every gap
 */
export function buildPageEntries(
  currentPage: number,
  pageCount: number,
  pageNeighbours: number = PAGE_NEIGHBOURS
): Array<number> {
  const setPage: Set<number> = new Set<number>([1, pageCount]);

  for (
    let page = currentPage - pageNeighbours;
    page <= currentPage + pageNeighbours;
    page++
  ) {
    if (page >= 1 && page <= pageCount) {
      setPage.add(page);
    }
  }

  const listPage: Array<number> = Array.from(setPage).sort(
    (pageA: number, pageB: number) => pageA - pageB
  );

  const listEntry: Array<number> = [];

  listPage.forEach((page: number, index: number) => {
    if (index > 0 && page - listPage[index - 1] > 1) {
      listEntry.push(null);
    }

    listEntry.push(page);
  });

  return listEntry;
}
